// File: src/components/friends/ProfileActionSheet.jsx
import React, { useEffect, useState } from "react";
import { FaFlag, FaFacebookF, FaInstagram } from "react-icons/fa";
import { SiBluesky } from "react-icons/si";
import ReportFlowSheet from "./ReportFlowSheet";
import { ReactComponent as TribeLogo } from '../assets/TribeLogo.svg';

function isPWA() {
  return (
    (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
    window.navigator.standalone === true
  );
}

const interestEmojis = {
  Hiking: "🥾",
  Painting: "🎨",
  Coding: "💻",
  Music: "🎵",
  Art: "🖼️",
  Reading: "📚",
  Travel: "✈️",
  Photography: "📸",
  Gaming: "🎮",
  Cooking: "🍳",
  Yoga: "🧘",
  Dancing: "💃",
  Sports: "🏀",
  Movies: "🎬",
  Fitness: "🏋️",
  Meditation: "🧘",
  "Board Games": "🎲",
  Gardening: "🌱",
};

export default function ProfileActionSheet({
  open,
  onClose,
  user,
  onUserBlocked,
  isHost = false,
}) {
  const [showReportSheet, setShowReportSheet] = useState(false);
  const scrollPositionRef = React.useRef(0);

  useEffect(() => {
    const onEsc = (e) => e.key === "Escape" && onClose?.();
    if (open) document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [open, onClose]);

  useEffect(() => {
    const themeMetaTag = document.querySelector('meta[name="theme-color"]');
    if (open || showReportSheet) {
      scrollPositionRef.current = window.scrollY || window.pageYOffset;
      document.body.style.top = `-${scrollPositionRef.current}px`;
      document.body.classList.add("overlay-active");
      document.documentElement.classList.add("overlay-active");
      document.getElementById("root").classList.add("overlay-active");
      if (themeMetaTag) themeMetaTag.content = "#999998";
    } else {
      const y = scrollPositionRef.current;
      document.body.classList.remove("overlay-active");
      document.documentElement.classList.remove("overlay-active");
      document.getElementById("root").classList.remove("overlay-active");
      document.body.style.top = "";
      window.scrollTo(0, y);
      if (themeMetaTag) themeMetaTag.content = "#ffffff";
    }
    return () => {
      const y = scrollPositionRef.current;
      document.body.classList.remove("overlay-active");
      document.documentElement.classList.remove("overlay-active");
      document.getElementById("root").classList.remove("overlay-active");
      document.body.style.top = "";
      window.scrollTo(0, y);
      const meta = document.querySelector('meta[name="theme-color"]');
      if (meta) meta.content = "#ffffff";
    };
  }, [open, showReportSheet]);

  if (!open) return null;

  if (!user) {
    return (
      <>
        <div style={styles.backdrop} onClick={onClose} />
        <div style={styles.sheetContainer}>
          <div style={styles.loadingContainer}>Loading user...</div>
        </div>
      </>
    );
  }

  const getProfilePicUrl = (u) => u?.profilePicUrl || "/default-avatar.png";
  const ageText = (() => {
    if (!user.dateOfBirth) return "";
    const dob = new Date(user.dateOfBirth);
    const now = new Date();
    let age = now.getFullYear() - dob.getFullYear();
    const m = now.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < dob.getDate())) age--;
    return `${age}`;
  })();
  const genderText = user.gender || "";
  const { facebook, bluesky, instagram } = user.socials || {};

  return (
    <>
      {!showReportSheet && (
        <>
          <div style={styles.backdrop} onClick={onClose} />
          <div style={styles.sheetContainer} onClick={(e) => e.stopPropagation()}>
            {/* NEW: SortBy-style header */}
            <div style={styles.headerBar}>
              <div style={styles.headerTitle}>Profile</div>
            
            </div>

            <div style={styles.scrollArea} className="hide-scrollbar">
              {/* Header Card */}
              <section style={styles.headerCard}>
                <div style={styles.headerRow}>
                  <img
                    src={getProfilePicUrl(user)}
                    alt="Profile"
                    style={styles.headerAvatar}
                  />
                  <div style={styles.headerTextCol}>
                    <div style={styles.nameRow}>
                      <h1 style={styles.name}>
                        {user.firstName} {user.lastName ? user.lastName.charAt(0) + "." : ""}
                      </h1>
                    </div>
                    {(ageText || genderText) && (
                      <div style={styles.metaAG}>
                        {[ageText || null, genderText || null].filter(Boolean).join(" • ")}
                      </div>
                    )}

                    {isHost && (
                      <span style={styles.hostPill}>
                        HOST
                      </span>
                    )}

                    {/* NEW: grey dotted viewer note */}
                    <div style={styles.viewingNote}>
                      Viewing Profile
                    </div>
                  </div>
                </div>
              </section>

              {/* About */}
              {user.overview && (
                <>
                  <h2 style={styles.sectionLabel}>About</h2>
                  <p style={styles.bodyText}>{user.overview}</p>
                  <div style={styles.sectionDivider} />
                </>
              )}

              {/* Interests */}
              {Array.isArray(user.interests) && user.interests.length > 0 && (
                <>
                  <h2 style={styles.sectionLabel}>Interests</h2>
                  <div style={styles.chipsWrap}>
                    {user.interests.map((i) => (
                      <span key={i} style={styles.chip}>
                        {interestEmojis[i] ? `${interestEmojis[i]} ${i}` : i}
                      </span>
                    ))}
                  </div>
                  <div style={styles.sectionDivider} />
                </>
              )}

              {/* Socials — icons only in bubbles, inline */}
              {(facebook || bluesky || instagram) && (
                <>
                  <h2 style={styles.sectionLabel}>Social</h2>
                  <div style={styles.socialsInlineRow}>
                    {facebook && (
                      <a
                        href={facebook}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={styles.socialBubble}
                        aria-label="Facebook"
                        title="Facebook"
                      >
                        <FaFacebookF style={styles.socialBubbleIcon} />
                      </a>
                    )}
                    {bluesky && (
                      <a
                        href={bluesky}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={styles.socialBubble}
                        aria-label="Bluesky"
                        title="Bluesky"
                      >
                        <SiBluesky style={styles.socialBubbleIcon} />
                      </a>
                    )}
                    {instagram && (
                      <a
                        href={instagram}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={styles.socialBubble}
                        aria-label="Instagram"
                        title="Instagram"
                      >
                        <FaInstagram style={styles.socialBubbleIcon} />
                      </a>
                    )}
                  </div>
                  <div style={styles.sectionDivider} />
                </>
              )}

              {/* Report */}
              <button style={styles.reportBtn} onClick={() => setShowReportSheet(true)}>
                <FaFlag size={12} style={{ marginRight: 6 }} />
                Report user
              </button>
            </div>
          </div>
        </>
      )}

      {showReportSheet && (
        <ReportFlowSheet
          user={user}
          onBackToProfile={() => setShowReportSheet(false)}
          onExitAll={() => {
            setShowReportSheet(false);
            onClose?.();
          }}
          onUserBlocked={(blockedId) => {
            setShowReportSheet(false);
            onUserBlocked?.(blockedId);
            onClose?.();
          }}
        />
      )}

      <style>
        {`
          @keyframes slideUp {
            from { transform: translateY(100%); opacity: 0; }
            to   { transform: translateY(0);     opacity: 1; }
          }
        `}
      </style>
    </>
  );
}

/* ----------------------------------
   Premium Minimal UI — Tight Scale (sheet)
---------------------------------- */
const styles = {
  backdrop: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(0,0,0,0.4)",
    zIndex: 998,
  },
  sheetContainer: {
    position: "fixed",
    left: 8,
    right: 8,
    bottom: 8,
    marginBottom: isPWA() ? 40 : 8,
    background: "#FFFFFF",
    border: "1px solid #E7E9EE",
    borderRadius: 12,
    animation: "slideUp 0.25s ease-out",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    zIndex: 999,
    maxHeight: "70vh",
  },

  /* NEW: SortBy-style header bar */
  headerBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    height: 48,
    padding: "0 16px",
    borderBottom: "1px solid #eef0f2",
  },
  headerTitle: {
    fontSize: 14,
    fontWeight: 800,
    color: "#0b0b0b",
    letterSpacing: "-0.01em",
  },
  headerResetBtn: {
    background: "#f3f4f6",
    border: "1px solid #e5e7eb",
    color: "#374151",
    borderRadius: 999,
    padding: "6px 12px",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
  },

  scrollArea: {
    flex: 1,
    overflowY: "auto",
    padding: 12,
  },
  loadingContainer: {
    padding: 16,
    textAlign: "center",
  },

  /* Header card */
  headerCard: {
    borderRadius: 12,
    background: "#FFFFFF",
    padding: 12,
    paddingLeft: 0,
    marginBottom: 14,
  },
  headerRow: {
    display: "grid",
    gridTemplateColumns: "96px 1fr",
    columnGap: 8,
    alignItems: "center",
  },
  headerAvatar: {
    width: 86,
    height: 86,
    borderRadius: "50%",
    objectFit: "cover",
    background: "#F3F4F6",
    border: "4px solid #ddd",
  },
  headerTextCol: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  },
  nameRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  name: {
    margin: 0,
    fontSize: "clamp(1.3rem, 2.2vw, 1.35rem)",
    fontWeight: 900,
    letterSpacing: "-0.005em",
    lineHeight: 1.5,
    color: "#0B1020",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  metaAG: {
    marginTop: 4,
    fontSize: 12.5,
    color: "#6B7280",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    lineHeight: 1.5,
  },

  hostPill: {
    display: "inline-flex",
    alignItems: "center",
    width: "fit-content",
    marginTop: 5,
    padding: "2px 6px",
    borderRadius: 999,
    backgroundColor: "#F3F4F6",
    color: "#6B7280",
    fontSize: 9,
    fontWeight: 800,
    letterSpacing: "0.06em",
    lineHeight: 1.2,
    marginLeft:-4,
  },

  /* NEW: viewer note (grey dotted box) */
  viewingNote: {
    marginTop: 8,
    background: "#FFFFFF",
    color: "#555",
    border: "1px dotted #999",
    borderRadius: 10,
    padding: "6px 10px",
    fontSize: 11.5,
    fontWeight: 800,
    display: "inline-flex",
    alignItems: "center",
    whiteSpace: "nowrap",
    marginLeft: -4,
    width: "fit-content",
  },

  /* Labels, text, dividers */
  sectionLabel: {
    margin: "14px 2px 8px",
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "#555",
    marginLeft: 5,
  },
  sectionDivider: {
    height: 1,
    backgroundColor: "#EEF0F2",
    margin: "12px 0",
  },
  bodyText: {
    margin: 0,
    fontSize: 13,
    lineHeight: 1.6,
    color: "#2E3238",
    marginLeft: 5,
    whiteSpace: "pre-line",
  },

  /* Chips */
  chipsWrap: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    marginLeft: 5,
  },
  chip: {
    display: "inline-block",
    padding: "6px 10px",
    borderRadius: 999,
    background: "#F7F8FA",
    color: "#0B1020",
    border: "1px solid #E7E9EE",
    fontSize: 12,
    fontWeight: 500,
    whiteSpace: "nowrap",
  },

  /* Socials as inline bubbles */
  socialsInlineRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginLeft: 5,
    padding: "2px 0",
  },
  socialBubble: {
    width: 36,
    height: 36,
    borderRadius: 999,
    display: "grid",
    placeItems: "center",
    border: "1px solid #E7E9EE",
    background: "#F7F8FA",
    textDecoration: "none",
  },
  socialBubbleIcon: {
    fontSize: 16,
    color: "#111",
    lineHeight: 1,
  },

  /* Report button */
  reportBtn: {
    alignSelf: "start",
    background: "#DC2626",
    color: "#fff",
    border: "none",
    borderRadius: 10,
    padding: "8px 12px",
    fontSize: 12,
    fontWeight: 900,
    display: "inline-flex",
    alignItems: "center",
    cursor: "pointer",
    marginLeft: 5,
  },
};